import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, MapPin } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const SITE = 'https://staymii.com';
const PAGE = '/press/beds24-e-invoice-soft-launch';
const DATE_ISO = '2026-07-16';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'StayMii Announces Beds24 e-Invoice Integration for Malaysia — Soft Launching 16 July 2026',
  description: 'StayMii launches a native Beds24 to LHDN MyInvois e-Invoice integration, automating tax-compliant invoicing for Malaysian hotels and short-term rentals.',
  datePublished: DATE_ISO,
  dateModified: DATE_ISO,
  mainEntityOfPage: SITE + PAGE,
  author: { '@type': 'Organization', name: 'StayMii', url: SITE },
  publisher: { '@type': 'Organization', name: 'StayMii', url: SITE, logo: { '@type': 'ImageObject', url: SITE + '/og-image.jpg' } },
  keywords: 'Beds24 e-Invoice Malaysia, Beds24 MyInvois Integration, LHDN e-Invoice for Beds24, Hotel e-Invoice Malaysia',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Press', item: SITE + '/press' },
    { '@type': 'ListItem', position: 3, name: 'Beds24 e-Invoice Soft Launch', item: SITE + PAGE },
  ],
};

const paras = [
  { h: 'FOR IMMEDIATE RELEASE', p: 'StayMii, the Malaysian hospitality e-invoicing and property-compliance platform, today announced the soft launch of its Beds24 e-Invoice integration for Malaysia, going live on 16 July 2026. The integration connects the widely used Beds24 property management system directly to LHDN\u2019s MyInvois platform, automating tax-compliant e-invoicing for hotels, serviced apartments and short-term rental operators nationwide.' },
  { h: 'Solving the Beds24 compliance gap', p: 'Beds24 is one of the most popular channel managers among Malaysian accommodation providers, yet it does not natively generate LHDN-compliant e-invoices. Since the July 2025 mandate extended e-Invoicing to all businesses regardless of revenue, Beds24 operators have had to manually re-key every booking into the MyInvois portal — a slow and error-prone process at scale. StayMii\u2019s Beds24 e-Invoice integration removes that manual work entirely.' },
  { h: 'How it works', p: 'Once an operator pastes their Beds24 API key into StayMii and connects their MyInvois credentials, every booking — whether from Booking.com, Agoda, Expedia, Airbnb or direct — is automatically converted into a structured LHDN e-invoice with correct SST, service charge and tourism-tax line items, submitted to MyInvois, and returned with an official validation UUID and QR code. Cancellations and rate changes trigger matching Credit and Debit Notes automatically.' },
  { h: 'Built for Malaysian hospitality', p: 'The integration handles the tax nuances specific to Malaysian hotels — 8% SST, 10% service charge, and RM10-per-room-per-night tourism tax for foreign guests — and supports historical backfill so operators can cover the mandate period. Setup takes under 15 minutes with no migration and no IT team required.' },
  { h: 'Availability', p: 'The Beds24 e-Invoice integration soft launches on 16 July 2026. Early access is open now, with a 14-day free trial and no credit card required. Operators can request a demo or sign up for early onboarding at staymii.com/integrations/beds24-e-invoice.' },
  { h: 'About StayMii', p: 'StayMii is a Malaysia-based platform providing LHDN e-Invoice automation, property management, and compliance tools for hotels, homestays and short-term rental operators. StayMii integrates with leading PMS and channel-manager platforms to make Malaysian tax compliance effortless.' },
];

export default function Beds24EInvoicePressRelease() {
  return (
    <div className="min-h-screen bg-white" data-testid="beds24-press-release-page">
      <SEO
        title="Press Release: StayMii Beds24 e-Invoice Integration Soft Launch (16 July 2026)"
        description="StayMii announces its Beds24 to LHDN MyInvois e-Invoice integration for Malaysia, soft launching 16 July 2026. Automated, tax-compliant invoicing for Beds24 hotels."
        keywords="beds24 e-invoice malaysia, beds24 myinvois integration, lhdn e-invoice for beds24, hotel e-invoice malaysia, staymii press release"
        canonicalUrl={PAGE}
      />
      <StructuredData id="beds24-press" schema={[articleSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <article className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/integrations/beds24-e-invoice" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Beds24 e-Invoice Malaysia</Link>
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> 16 July 2026</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Kuala Lumpur, Malaysia</span>
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">Press Release</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">StayMii Announces Beds24 e-Invoice Integration for Malaysia — Soft Launching 16 July 2026</h1>
        <figure className="mt-8" data-testid="press-hero-image">
          <img
            src="https://customer-assets.emergentagent.com/job_d5f90b05-f1ef-48e9-9d70-17d55980b85e/artifacts/qs3monj2_26608CC9-410D-493E-8C97-BDC7AF9B91AF.png"
            alt="StayMii partners with Beds24 for e-Invoicing submission and compliance"
            className="w-full rounded-2xl border border-slate-200 shadow-sm"
            loading="lazy"
          />
          <figcaption className="mt-3 text-center text-sm text-slate-500">StayMii partners with Beds24 for e-Invoicing submission and compliance</figcaption>
        </figure>
        <div className="mt-8 space-y-7">
          {paras.map((s, i) => (
            <div key={i}>
              <h2 className="text-base md:text-lg font-semibold text-slate-900">{s.h}</h2>
              <p className="text-slate-600 mt-2 leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center">
          <p className="font-semibold text-slate-900">Media & early-access enquiries</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" data-testid="press-request-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">Request a Demo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/auth/signup" data-testid="press-early-access" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-semibold border border-slate-300 hover:bg-slate-100">Get Early Access</Link>
          </div>
        </div>
      </article>

      <LandingFooter />
    </div>
  );
}
